<template>
  <Card class="mb-6">
    <CardHeader>
      <CardTitle>Photos</CardTitle>
    </CardHeader>
    <CardContent>
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <div
          v-for="(photo, index) in normalizedPhotos"
          :key="index"
          class="aspect-square relative cursor-pointer overflow-hidden rounded-lg"
          @click="openPhotoModal(index)"
        >
          <NuxtImg
            :src="photo.url"
            :alt="title"
            class="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
          />
          <div
            class="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center"
          >
            <Icon
              name="mdi:magnify"
              class="text-white opacity-0 hover:opacity-100 transition-opacity duration-300"
              size="24"
            />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>

  <Dialog v-model:open="isPhotoModalOpen">
    <DialogContent class="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw] xl:max-w-[60vw] p-0">
      <DialogHeader
        class="p-4 absolute top-0 left-0 right-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      >
        <div class="flex justify-between items-center">
          <DialogTitle>Photo Gallery</DialogTitle>
          <Button variant="ghost" size="icon" @click="closePhotoModal">
            <Icon name="heroicons:x-mark" class="h-4 w-4" />
          </Button>
        </div>
      </DialogHeader>

      <div class="h-[calc(100vh-8rem)] mt-14">
        <Carousel v-model="currentPhotoIndex" class="w-full h-full">
          <CarouselContent>
            <CarouselItem
              v-for="(photo, index) in normalizedPhotos"
              :key="index"
              class="h-full flex items-center justify-center p-4"
            >
              <div class="relative w-full h-full flex items-center justify-center">
                <NuxtImg
                  :src="photo.url"
                  :alt="title"
                  class="max-w-full max-h-full object-contain rounded-md"
                  loading="lazy"
                />
              </div>
            </CarouselItem>
          </CarouselContent>
          <template v-if="normalizedPhotos.length > 1">
            <CarouselPrevious class="left-2" />
            <CarouselNext class="right-2" />
          </template>
        </Carousel>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel'
import { Button } from '@/components/ui/button'

interface Photo {
  url: string
  width?: number
  height?: number
}

interface PhotosObject {
  [key: string]: Photo
}

const props = defineProps<{
  photos: Photo[] | PhotosObject
  title: string
}>()

const normalizedPhotos = computed<Photo[]>(() => {
  if (!props.photos) return []

  // If it's already an array, return it
  if (Array.isArray(props.photos)) {
    return props.photos
  }

  // If it's an object, transform it to array
  return Object.values(props.photos).map((photo) => ({
    url: photo.url,
    width: photo.width,
    height: photo.height,
  }))
})

const isPhotoModalOpen = ref(false)
const currentPhotoIndex = ref(0)

const openPhotoModal = (index: number) => {
  currentPhotoIndex.value = index
  isPhotoModalOpen.value = true
}

const closePhotoModal = () => {
  isPhotoModalOpen.value = false
}
</script>
